import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface ReassignTeacherProps {
  domain: string;
}

interface Teacher {
  id: string;
  name: string;
  employeeId: string;
  email: string;
  phone: string;
  classAssigned?: string[];
  subjects?: string[];
}

interface Subject {
  subjectName: string;
  className: string;
}

interface TimetableSlot {
  period: string;
  subject: string;
  teacher: string;
  teacherId: string;
  startTime: string;
  endTime: string;
}

const ReassignTeacher: React.FC<ReassignTeacherProps> = ({ domain }) => {
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  const [selectedTeacher, setSelectedTeacher] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedSection, setSelectedSection] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedDay, setSelectedDay] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState('');

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const periods = ['Period 1', 'Period 2', 'Period 3', 'Period 4', 'Period 5', 'Period 6', 'Period 7', 'Period 8'];

  useEffect(() => {
    loadTeachers();
    loadSubjects();
  }, [domain]);

  const loadTeachers = async () => {
    setLoading(true);
    try {
      const teachersRef = collection(db, `school-users/${domain}/teachers`);
      const snapshot = await getDocs(teachersRef);

      const teachersList: Teacher[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as Teacher));

      setTeachers(teachersList.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error: any) {
      console.error('Error loading teachers:', error);
      alert(`Failed to load teachers: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadSubjects = async () => {
    try {
      const subjectsRef = collection(db, `school-data/${domain}/subjects`);
      const snapshot = await getDocs(subjectsRef);

      const subjectsList: Subject[] = [];
      snapshot.forEach((doc) => {
        const data = doc.data();
        if (data.isActive) {
          subjectsList.push({
            subjectName: data.subjectName,
            className: data.className,
          });
        }
      });

      setSubjects(subjectsList);
    } catch (error: any) {
      console.error('Error loading subjects:', error);
    }
  };

  const getSubjectsForClass = () => {
    if (!selectedClass) return [];
    return subjects.filter(s => s.className === selectedClass);
  };

  const handleReassign = async () => {
    if (!selectedTeacher || !selectedClass || !selectedSection || !selectedSubject) {
      alert('Please select Teacher, Class, Section, and Subject!');
      return;
    }

    if (!confirm(`Are you sure you want to reassign this teacher to ${selectedSubject} for Class ${selectedClass}-${selectedSection}?`)) {
      return;
    }

    setProcessing(true);

    try {
      const teacher = teachers.find(t => t.id === selectedTeacher);
      if (!teacher) throw new Error('Teacher not found');

      const classKey = `${selectedClass}${selectedSection}`;

      // 1. Update Teacher Profile - Add to classAssigned
      const teacherRef = doc(db, `school-users/${domain}/teachers`, selectedTeacher);
      const teacherDoc = await getDoc(teacherRef);

      if (teacherDoc.exists()) {
        const currentData = teacherDoc.data();
        const classAssigned = currentData.classAssigned || [];
        const subjects = currentData.subjects || [];

        // Add class if not already assigned
        if (!classAssigned.includes(classKey)) {
          classAssigned.push(classKey);
        }

        // Add subject if not already assigned
        if (!subjects.includes(selectedSubject)) {
          subjects.push(selectedSubject);
        }

        await updateDoc(teacherRef, {
          classAssigned,
          subjects,
          updatedAt: new Date().toISOString(),
        });
      }

      // 2. Update Timetable if day and period are selected
      if (selectedDay && selectedPeriod) {
        const dayLower = selectedDay.toLowerCase();
        const timetableId = `class${selectedClass.toLowerCase()}${selectedSection.toLowerCase()}_${dayLower}`;
        const timetablePath = `school-users/${domain}/timetables`;
        const timetableRef = doc(db, timetablePath, timetableId);

        const timetableDoc = await getDoc(timetableRef);

        if (timetableDoc.exists()) {
          // Update existing timetable
          const timetableData = timetableDoc.data();
          const slots = timetableData.slots || [];

          // Find and update the specific period
          const periodIndex = slots.findIndex((slot: TimetableSlot) => slot.period === selectedPeriod);

          if (periodIndex >= 0) {
            slots[periodIndex] = {
              ...slots[periodIndex],
              subject: selectedSubject,
              teacher: teacher.name,
              teacherId: teacher.employeeId || teacher.id,
            };
          } else {
            // Add new slot if period doesn't exist
            slots.push({
              period: selectedPeriod,
              subject: selectedSubject,
              teacher: teacher.name,
              teacherId: teacher.employeeId || teacher.id,
              startTime: '',
              endTime: '',
            });
          }

          await updateDoc(timetableRef, {
            slots,
            updatedAt: new Date().toISOString(),
          });
        } else {
          // Create new timetable document
          await setDoc(timetableRef, {
            class: selectedClass,
            section: selectedSection,
            day: selectedDay,
            slots: [
              {
                period: selectedPeriod,
                subject: selectedSubject,
                teacher: teacher.name,
                teacherId: teacher.employeeId || teacher.id,
                startTime: '',
                endTime: '',
              },
            ],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          });
        }
      }

      alert(
        `Successfully reassigned ${teacher.name} to:\n\n` +
        `Class: ${selectedClass}-${selectedSection}\n` +
        `Subject: ${selectedSubject}` +
        (selectedDay && selectedPeriod ? `\nDay: ${selectedDay}\nPeriod: ${selectedPeriod}` : '')
      );

      // Reset form
      setSelectedTeacher('');
      setSelectedClass('');
      setSelectedSection('');
      setSelectedSubject('');
      setSelectedDay('');
      setSelectedPeriod('');

      // Reload teachers
      loadTeachers();
    } catch (error: any) {
      console.error('Error reassigning teacher:', error);
      alert(`Failed to reassign teacher: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  const selectedTeacherData = teachers.find(t => t.id === selectedTeacher);
  const availableSubjects = getSubjectsForClass();

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-white py-3">
        <h5 className="mb-0">
          <i className="bi bi-arrow-left-right me-2 text-primary-custom"></i>
          Reassign Teacher
        </h5>
        <small className="text-muted">Assign or reassign teachers to classes and subjects</small>
      </div>

      <div className="card-body">
        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading teachers...</p>
          </div>
        ) : (
          <>
            <div className="row g-3">
              {/* Teacher Selection */}
              <div className="col-md-6">
                <label className="form-label">
                  <i className="bi bi-person me-2"></i>
                  Select Teacher *
                </label>
                <select
                  className="form-select"
                  value={selectedTeacher}
                  onChange={(e) => setSelectedTeacher(e.target.value)}
                >
                  <option value="">Choose a teacher...</option>
                  {teachers.map((teacher) => (
                    <option key={teacher.id} value={teacher.id}>
                      {teacher.name} ({teacher.employeeId || teacher.id})
                    </option>
                  ))}
                </select>
                {selectedTeacherData && (
                  <div className="mt-2">
                    <small className="text-muted">
                      <strong>Current Classes:</strong>{' '}
                      {selectedTeacherData.classAssigned?.join(', ') || 'None'}
                      <br />
                      <strong>Current Subjects:</strong>{' '}
                      {selectedTeacherData.subjects?.join(', ') || 'None'}
                    </small>
                  </div>
                )}
              </div>

              {/* Class Selection */}
              <div className="col-md-3">
                <label className="form-label">
                  <i className="bi bi-building me-2"></i>
                  Class *
                </label>
                <select
                  className="form-select"
                  value={selectedClass}
                  onChange={(e) => {
                    setSelectedClass(e.target.value);
                    setSelectedSubject('');
                  }}
                >
                  <option value="">Select Class</option>
                  {[...Array(12)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      Class {i + 1}
                    </option>
                  ))}
                </select>
              </div>

              {/* Section Selection */}
              <div className="col-md-3">
                <label className="form-label">
                  <i className="bi bi-grid me-2"></i>
                  Section *
                </label>
                <select
                  className="form-select"
                  value={selectedSection}
                  onChange={(e) => setSelectedSection(e.target.value)}
                >
                  <option value="">Select Section</option>
                  {['A', 'B', 'C', 'D', 'E'].map((sec) => (
                    <option key={sec} value={sec}>
                      {sec}
                    </option>
                  ))}
                </select>
              </div>

              {/* Subject Selection */}
              <div className="col-md-12">
                <label className="form-label">
                  <i className="bi bi-book me-2"></i>
                  Subject *
                </label>
                <select
                  className="form-select"
                  value={selectedSubject}
                  onChange={(e) => setSelectedSubject(e.target.value)}
                  disabled={!selectedClass}
                >
                  <option value="">
                    {selectedClass ? 'Select Subject' : 'Please select a class first'}
                  </option>
                  {availableSubjects.map((subject, idx) => (
                    <option key={idx} value={subject.subjectName}>
                      {subject.subjectName}
                    </option>
                  ))}
                </select>
                {selectedClass && availableSubjects.length === 0 && (
                  <small className="text-warning d-block mt-1">
                    <i className="bi bi-exclamation-triangle me-1"></i>
                    No subjects configured for Class {selectedClass}. Please configure subjects first.
                  </small>
                )}
              </div>

              {/* Timetable Assignment (Optional) */}
              <div className="col-md-12">
                <div className="card bg-light">
                  <div className="card-body">
                    <h6 className="mb-3">
                      <i className="bi bi-calendar-week me-2"></i>
                      Timetable Assignment (Optional)
                    </h6>
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label">Day</label>
                        <select
                          className="form-select"
                          value={selectedDay}
                          onChange={(e) => setSelectedDay(e.target.value)}
                        >
                          <option value="">Select Day (Optional)</option>
                          {days.map((day) => (
                            <option key={day} value={day}>
                              {day}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">Period</label>
                        <select
                          className="form-select"
                          value={selectedPeriod}
                          onChange={(e) => setSelectedPeriod(e.target.value)}
                          disabled={!selectedDay}
                        >
                          <option value="">
                            {selectedDay ? 'Select Period (Optional)' : 'Select Day first'}
                          </option>
                          {periods.map((period) => (
                            <option key={period} value={period}>
                              {period}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <small className="text-muted d-block mt-2">
                      <i className="bi bi-info-circle me-1"></i>
                      Selecting day and period will automatically update the timetable
                    </small>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="mt-4">
              <button
                className="btn btn-primary btn-lg w-100"
                onClick={handleReassign}
                disabled={processing || !selectedTeacher || !selectedClass || !selectedSection || !selectedSubject}
              >
                {processing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-circle me-2"></i>
                    Reassign Teacher
                  </>
                )}
              </button>
            </div>

          </>
        )}
      </div>
    </div>
  );
};

export default ReassignTeacher;
